import { formatLineStatusLabel } from '@/components/pos/posLineStatus'
import { POS_LINE_OPERATIONS, POS_TICKET_OPERATIONS } from '@/components/pos/posOperations'
import { SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'
import { toPosAmount } from '@/components/pos/posCatalog'
import { cn } from '@/lib/utils'

const TONE_CLASSES = {
  default: 'border-[#d1d5db] bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50',
  amber: 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100',
  red: 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100',
  primary: SALON_DESK_CHECKOUT.lite
    ? 'border-slate-300 bg-slate-50 text-slate-800 hover:bg-slate-100'
    : 'border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100',
}

/**
 * Operations grid for the right POS column (light theme).
 *
 * @param {object} props
 * @param {import('@/types/api').SaleLine | null} props.selectedLine
 * @param {import('@/types/api').Stylist[]} props.stylists
 * @param {boolean} props.disabled
 * @param {boolean} [props.hasAddons]
 * @param {(actionId: string) => void} props.onAction
 */
export function PosOperationsPanel({
  selectedLine,
  stylists,
  disabled,
  hasAddons = false,
  onAction,
}) {
  const assignedStylist = selectedLine
    ? stylists.find((stylist) => stylist.id === selectedLine.stylist_id)
    : null

  const lineOperations = SALON_DESK_CHECKOUT.showOperationsPanel
    ? POS_LINE_OPERATIONS
    : POS_LINE_OPERATIONS.filter((action) =>
        SALON_DESK_CHECKOUT.lite
          ? ['stylist', 'delete', 'lineDiscount'].includes(action.id)
          : ['stylist', 'delete'].includes(action.id),
      )

  const ticketOperations = SALON_DESK_CHECKOUT.showOperationsPanel
    ? POS_TICKET_OPERATIONS
    : POS_TICKET_OPERATIONS.filter((action) =>
        SALON_DESK_CHECKOUT.lite
          ? ['hold', 'recall', 'billDiscount'].includes(action.id)
          : ['hold', 'recall', 'billDiscount'].includes(action.id),
      )

  if (SALON_DESK_CHECKOUT.lite && !SALON_DESK_CHECKOUT.showOperationsPanel && lineOperations.length === 0 && ticketOperations.length === 0) {
    return null
  }

  return (
    <div className="pos-v2-ops border-b-0 bg-white">
      <div className="pos-v2-ops-header shrink-0 border-b border-[#E5E7EB] bg-[#F8FAFC] px-2 py-1 font-bold uppercase tracking-wider text-slate-500">
        {SALON_DESK_CHECKOUT.lite ? 'Selected item' : 'Operations'}
      </div>

      <div className="p-1.5">
        {selectedLine ? (
          <div
            className={cn(
              'mb-1.5 rounded-md border px-2 py-1.5',
              SALON_DESK_CHECKOUT.lite
                ? 'border-slate-200 bg-slate-50'
                : 'border-primary/30 bg-primary/5',
            )}
          >
            <p className="pos-v2-ui-body truncate font-bold text-slate-900">{selectedLine.description}</p>
            <p className="pos-v2-ui-meta mt-0.5 text-slate-600">
              {SALON_DESK_CHECKOUT.lite ? (
                <>
                  Qty {selectedLine.quantity}
                  {' · '}
                  {assignedStylist?.name ?? 'No stylist yet'}
                  {' · '}
                  AED {toPosAmount(selectedLine.line_total).toFixed(2)}
                </>
              ) : (
                <>
                  Qty {selectedLine.quantity}
                  {' · '}
                  {assignedStylist?.name ?? 'No stylist'}
                  {' · '}
                  Split {selectedLine.stylist_split_percent ?? 100}%
                  {' · '}
                  {selectedLine.line_type === 'service'
                    ? formatLineStatusLabel(selectedLine.line_status)
                    : 'Retail'}
                  {' · '}
                  AED {toPosAmount(selectedLine.line_total).toFixed(2)}
                </>
              )}
            </p>
          </div>
        ) : null}

        <div className="mb-1.5">
          <div className="pos-v2-ops-grid">
            {lineOperations.map((action) => (
              <OperationButton
                key={action.id}
                action={action}
                disabled={disabled || (action.requiresLine && !selectedLine) || (action.id === 'addon' && !hasAddons)}
                onClick={() => onAction(action.id)}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="pos-v2-ui-label mb-1 px-0.5 font-bold uppercase tracking-wide text-slate-500">Ticket</p>
          <div className="pos-v2-ops-grid">
            {ticketOperations.map((action) => (
              <OperationButton
                key={action.id}
                action={action}
                disabled={disabled}
                onClick={() => onAction(action.id)}
                fullWidth={action.fullWidth}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * @param {object} props
 * @param {import('./posOperations').PosOperationDef} props.action
 * @param {boolean} props.disabled
 * @param {() => void} props.onClick
 * @param {boolean} [props.fullWidth]
 */
function OperationButton({ action, disabled, onClick, fullWidth = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'pos-v2-op-btn flex flex-col items-center justify-center rounded-md border px-0.5 font-semibold leading-tight transition-colors active:translate-y-px disabled:cursor-not-allowed disabled:opacity-40',
        fullWidth && 'pos-v2-op-btn--full',
        TONE_CLASSES[action.tone ?? 'default'],
      )}
    >
      <span className="text-center">
        {SALON_DESK_CHECKOUT.lite && action.id === 'stylist' ? 'Staff' : action.id === 'delete' && SALON_DESK_CHECKOUT.lite ? 'Remove' : action.label}
      </span>
      {action.shortcut && !SALON_DESK_CHECKOUT.showKeyboardShortcuts ? null : action.shortcut ? (
        <span className="pos-v2-op-shortcut font-mono">{action.shortcut}</span>
      ) : null}
    </button>
  )
}
